import { Button } from "components/Button/Button";
import { Callout } from "components/Form/components/Callout/Callout";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { Input } from "components/Form/components/Input/Input";
import { WebhookSecurityPopover } from "components/Form/components/WebhookSecurity/WebhookSecurityPopover";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover } from "components/Popover/Popover";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { useResourceData } from "containers/Resources/screens/ResourcesSingle/useResourceData";
import { Webhook } from "graphql/generated/graphql";
import { useState } from "react";
import "./WebhookSecurity.scss";
import { WebhookSecurityInnerForm } from "./WebhookSecurityInnerForm";

export const WebhookSecurityFormComponent = ({
    input,
    meta,
    fieldProps,
    fieldAttr,
    fetchResourceData,
}: FormComponentProps) => {
    const { isReadOnly, isDisabled, resourceData } = useResourceData();
    const [isOpened, setOpened] = useState(false);
    const closePopover = () => setOpened(false);

    const isButtonDisabled = isReadOnly || isDisabled;

    if (!resourceData) {
        return null;
    }

    return (
        <Input fieldProps={fieldProps} fieldAttr={fieldAttr} input={input} meta={meta}>
            <div className="webhookSecurityContainer">
                <div className="webhookSecurityHeader">
                    <span>Webhook security</span>
                    <Popover
                        trigger={
                            <Button
                                label="Edit webhook security"
                                icon={IconCollection.shieldKeyOutline}
                                onClick={() => setOpened(true)}
                                disabled={isButtonDisabled}
                                variation={"dark"}
                            />
                        }
                        closeOnInsideClick={false}
                        isOpen={isOpened}
                        toggleClassName={`${isButtonDisabled ? "disabled" : ""}`}
                        anchorReference="none"
                        rootClassName="webhookSecurityPopover"
                        contentClassName="webhookSecurityPopoverContent"
                    >
                        <WebhookSecurityPopover
                            closePopover={closePopover}
                            fetchResourceData={fetchResourceData}
                            resourceData={resourceData as Webhook}
                            isReadOnly={false}
                        />
                    </Popover>
                    <DocsInfoTooltip
                        title={"Webhook configuration"}
                        urlPath={"/docs/webhooks#configuration"}
                    />
                </div>
                <WebhookSecurityInnerForm isReadOnly={true} displayValues={input.value} />
                <div className="webhookCallouts">
                    {input.value.require_login === false && input.value.is_key_required === false ? (
                        <Callout
                            text="WARNING: This webhook is unprotected. Please set a key or require login token."
                            type={"warning"}
                        />
                    ) : null}
                    {input.value.is_key_required === true ? (
                        <Callout
                            type="info"
                            text={
                                <>
                                    <strong>Providing the key — choose one of:</strong>
                                    <ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
                                        <li>
                                            <strong>Bearer token</strong> (recommended):{" "}
                                            <code>Authorization: Bearer &lt;key&gt;</code>
                                        </li>
                                        <li>
                                            <strong>JSON body</strong>: add{" "}
                                            <code>"key": "&lt;key&gt;"</code> to the JSON request
                                            body — only works if the body is a JSON object and{" "}
                                            <code>key</code> does not conflict with your payload
                                            fields.
                                        </li>
                                        <li>
                                            <strong>Query parameter</strong> (discouraged):{" "}
                                            <code>?key=&lt;key&gt;</code> — URL parameters may
                                            appear in server logs.
                                        </li>
                                    </ul>
                                </>
                            }
                        />
                    ) : null}
                    {input.value.require_login === true ? (
                        <Callout
                            type="info"
                            text={
                                <>
                                    <strong>Providing the login token:</strong> When calling this
                                    webhook from a browser where you are already logged in to
                                    Cloudomation, the session cookie is sent automatically. For
                                    other clients, first obtain a session cookie by logging in,
                                    then include it in the request as an HTTP header:{" "}
                                    <code>Cookie: session=&lt;value&gt;</code>
                                </>
                            }
                        />
                    ) : null}
                </div>
            </div>
        </Input>
    );
};
